/* eslint-disable  @typescript-eslint/no-explicit-any */
import { Component, Host, h, Prop, State, Watch, Event, type EventEmitter, Element, Method } from '@stencil/core';
import type { FacetChildUiModel } from '../../../../../models/ui/facets/facet-tree-ui.model';
import type { FilterNode } from '../../../../result-page/facets-build-wrapper/filter-node';
import type { BooleanString } from '../../mobile-filters';
import {filter} from 'rxjs';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import {FacetTag} from '../../../../result-page/facets/subcomponents/facet-tag/facet-tag';
import {FACET_TAG} from '../../../../result-page/facets/subcomponents/facet-tag/facet-tags';
@Component({
  tag: 'filters-tree-node',
  styleUrl: 'filters-tree-node.scss',
  shadow: false,
  scoped: true
})
export class FiltersTreeNode {
  @Element() el!: HTMLElement;

  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @Prop() readonly rtl: boolean;
  @Prop({attribute: 'level'}) readonly level: number;
  @Prop({attribute: 'show-input'}) readonly showInputProp: BooleanString = 'true';
  @Prop({attribute: 'tree-node'}) readonly treeNodeProp!: string;
  @Watch('treeNodeProp')
  onTreeNodeChange(newValue: string) {
      if(newValue) {
        this.createLocalState(JSON.parse(newValue));
        this.children = (JSON.parse(newValue)).children;
      }
  }
  @Prop({attribute: 'show-header'}) readonly showHeaderProp: BooleanString = 'true';
  @Prop({attribute: 'search-filter-label'}) readonly searchFilterLabel: string;
  @Prop({attribute: 'is-root'}) readonly isRootProp!: BooleanString;
  @Prop() readonly filterByLocalChipsState: boolean = false;
  @Prop() readonly forceCollapse: boolean = false;
  @Prop({attribute: 'facet-local-chips'}) readonly facetLocalChipsProp: FilterNode[] = [];
  @Watch('facetLocalChipsProp')
  onFacetLocalChips(newValue: FilterNode[]): void {
      this.facetLocalChipsState = newValue;
  }

  @State() canClear = false;
  @State() collapsed = false;
  @State() localFiltersState: FilterNode[] = [];
  @State() children: FilterNode[] = [];
  @State() facetLocalChipsState: FilterNode[] = [];

  private treeNode: FilterNode;
  private isRoot: boolean;
  private showInput: boolean;

  @Event({bubbles: false}) filterChange: EventEmitter<FilterNode>;
  @Event({bubbles: false}) collapse: EventEmitter<{element: HTMLElement, collapse: boolean, filtersCount: number}>;

  @Method()
  async collapseClose(): Promise<void> {
      this.collapsed = false;
      // @ts-ignore
      [...this.el.querySelectorAll('filters-tree-node')].forEach((element) => {
          element.collapseClose();
      });
  }

  @Method()
  async clearSelected() {
      this.localFiltersState = [];
      // @ts-ignore
      for(const element of this.el.querySelectorAll('filter-checkbox')) {
        (element as any).setSelected(false);
      }
  }

  private inputElement: HTMLInputElement;
  private headerWrapperElement: HTMLElement;
  private contentWrapperElement: HTMLElement;
  private inputSearchWrapperHeight = 60;

  private onInput(event: Event) {
      const value = (this.inputElement as HTMLInputElement).value;
      this.filterChildren(value);
      this.canClear = value.length > 0;
  }


  private onClickCollapse(): void {
      this.collapsed = !this.collapsed;
      this.collapse.emit({element: this.el, collapse: this.collapsed, filtersCount: this.children.length});
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            return fallback;
        };
      }
      this.treeNode = JSON.parse(this.treeNodeProp);
      this.children = this.treeNode.children;
      this.isRoot = this.isRootProp === 'true';
      this.showInput = this.showInputProp === 'true';
      this.createLocalState(this.treeNode);

      if(this.forceCollapse) {
        this.collapsed = true;
      }
  }

  componentWillUpdate() {
      this.treeNode = JSON.parse(this.treeNodeProp);
      this.showInput = this.showInputProp === 'true';
      this.isRoot = this.isRootProp === 'true';
  }

  componentDidRender(): void {
      this.calcuateAndSetContentHeight();
  }

  render() {
      return (
        <Host>
          {(this.treeNode.children?.length > 0)
            ? this.renderHeader()
            : ''}
          <div class={`ftn-content-wrapper ${(this.collapsed || !this.showHeaderProp) ? 'visible' : ''}`} ref={e => this.contentWrapperElement = e}>
            {(this.showInput) ? <div class={'ftn-filters-input'}>
              <qds-input
                class='ftn-input'
                type="search"
                placeholder={this.searchFilterLabel}
                onQdsInput={(e) => this.onInput(e)}
                ref={el => this.inputElement = el as HTMLInputElement}
              />
            </div> : ''}
            <div class={'ftn-checkbox-list-wrapper'}>
              {this.renderFormControls()}
            </div>
            <slot />
          </div>
        </Host>
      );
  }

  private isFilterInLocalChipsStateNotSelected(filter: FilterNode): boolean {
      return this.facetLocalChipsState.filter((fl) => fl.uniqueId === filter.uniqueId).length === 0;
  }

  private createLocalState(treeNode: FilterNode): void {
      const localFiltersState = [];
      treeNode.children.forEach((node) => {
          if(node.selected && !localFiltersState.some((chStNode) => chStNode.uniqueId === node.uniqueId)) {
            localFiltersState.push(node);
          } else if(!node.selected) {
            const index = localFiltersState.findIndex(filter => filter.uniqueId === node.uniqueId);
            if(index !== -1) {
              localFiltersState.splice(index, 1);
            }
          }
      });
      this.localFiltersState = localFiltersState;
  }

  private renderFormControls() {
      return (this.children.map((filter: FacetChildUiModel, index) => (
        <filter-checkbox
          key={index + filter.uniqueId}
          title={(this.labelProvider(filter.value, filter.displayName))}
          has-paddings={'true'}
          selectedProp={this.filterByLocalChipsState
            ? this.isFacetNodeNotInState(filter) ? 'false' : 'true'
            : filter.selected ? 'true' : 'false'}
          labelProp={`${this.makeHighlight(filter.displayName)} <span class="filter-count">(${filter.count})</span>`}
          rtl={this.rtl}
          onSelectChange={(e) => {
              this.addOrRemoveFilter(filter as FilterNode, e.detail);
              this.filterChange.emit({...filter, ...{selected: e.detail}} as FilterNode);
          }}
        ></filter-checkbox>
      )));
  }

  private isFacetNodeNotInState(filter) {
      return this.facetLocalChipsState.filter((fl) => fl.uniqueId === filter.uniqueId).length === 0;
  }

  private facetTags(facetId: string, facetDidplayName?: string) {
      return [
        <FacetTag
          isLabelVisible={facetId == FACET_TAG.CUSTOMIZATION}
          endDate='2025/01/31'
          label={this.labelProvider('additional-conditions.banner.new', 'New')} />,
        <FacetTag
          isLabelVisible={facetDidplayName == FACET_TAG.COMPLIANCE}
          endDate='2025/04/28'
          label={this.labelProvider('additional-conditions.banner.new', 'New')} />
      ];
  }

  private renderHeader() {
      return (
        <div
          class={`ftn-header-wrapper ${this.localFiltersState.length > 0 ? 'has-selected' : ''}`}
          onClick={() => this.onClickCollapse()}
          ref={(e) => this.headerWrapperElement = e}
        >
          <div class={'ftn-info-wrapper'}>
            <div class={'ffn-header-label-wraper'}>
              <div class={'ftn-title'}>{this.labelProvider(this.treeNode.value, this.treeNode.displayName)}</div>
              {this.facetTags(this.treeNode?.id, this.treeNode?.displayName)}
            </div>
            {this.localFiltersState.length > 0 ? <span class={'ftn-selected-list'}>
              {this.renderLocalState()}
            </span> : ''}
          </div>
          <div class={'buttons-wrapper'}>
            {this.collapsed
              ? <se-icon
                size="small"
              >
              arrow2_up
              </se-icon>
              : <se-icon
                size="small"
              >
              arrow2_down
              </se-icon> }
          </div>
        </div>
      );
  }

  private renderLocalState() {
      return (this.localFiltersState
        .filter((filter: FilterNode) => !this.isFacetNodeNotInState(filter))
        .map((filter: FilterNode, index: number) => (
          `${filter.displayName}${index !== this.localFiltersState.length - 1 ? ', ' : ''}`
        )));
  }

  private filterChildren(value: string): void {
      this.children.map((child, index)=> {
          const bool = (new RegExp(`^.*${value}.*$`, 'gi')).test(child.displayName);
          if(!bool) {
            this.el.querySelectorAll('filter-checkbox')[index].classList.add('filtered-hide');
          } else {
            this.el.querySelectorAll('filter-checkbox')[index].classList.remove('filtered-hide');
          }
      });
  }

  private addOrRemoveFilter(filter: FilterNode, add: boolean) {
      const localFiltersState = [...this.localFiltersState];
      if(add){
        localFiltersState.push(filter);
      } else {
        const index = localFiltersState.findIndex((node) => node.uniqueId === filter.uniqueId);
        if(index !== null) {
          localFiltersState.splice(index, 1);
        }
      }
      this.localFiltersState = localFiltersState;
  }

  private makeHighlight(label: string): string {
      if(!this.inputElement || this.inputElement.value === '')
        return label;

      const value = this.inputElement.value;
      const regex = new RegExp(`(${value})`, 'gi');
      let newLabel = label;

      const matches = [...label.matchAll(regex)];
      if(matches) {
        let indexRange = 0;
        Object.values(matches).forEach((matchData) => {
            const startIndex = (indexRange === 0) ? matchData.index : matchData.index + indexRange;
            newLabel = newLabel.substring(0, startIndex)
              + '<span class="highlight">' + matchData[0] + '</span>'
              + newLabel.substring(startIndex + matchData[0].length);
            indexRange += '<span class="highlight"></span>'.length;
        });

        return `<span class="has-highlight">${newLabel}</span>`;
      }

      return label;
  }

  private calcuateAndSetContentHeight(): void {
      const topHeight = 54;
      const bottmHeight = 72;
      const headerWrapperHeight = this.headerWrapperElement ? this.headerWrapperElement.offsetHeight : 49;
      const inputHeight = (this.showInput) ? this.inputSearchWrapperHeight : 0;
      const resetHeight = document.querySelector('filters-reset') ? document.querySelector('filters-reset').offsetHeight : 0;
      const totalHeight = topHeight + bottmHeight + headerWrapperHeight + inputHeight + resetHeight;
      if((window.innerHeight - totalHeight) <= (this.treeNode.children.length * 40)) {
        this.contentWrapperElement.style.height = `calc(100vh - ${totalHeight}px)`;
      }
  }
}
